import { ImageRetry } from '@openeducation/oe-rn-components';
import React, { useEffect, useMemo } from 'react';
import { Image, View, StyleSheet, StyleProp, ViewStyle } from 'react-native';

import { SizeBreakpoint } from '../../enums/size-breakpoint';
import { useColors } from '../../hooks';
import { useSafeState } from '../../hooks/use-safe-state';
import maxContainerWidthStyle from '../../styles/max-container-width/max-container-width.style';
import LessonAccentBanner from '../lesson-accent-banner/lesson-accent-banner';
import { breakpoint } from '../media-query';

interface LessonImageProps {
  imageUrl?: string;
  showBanner?: boolean;
  style?: StyleProp<ViewStyle>;
}

const LessonImage: React.FC<LessonImageProps> = ({
  imageUrl,
  showBanner = true,
  style,
}) => {
  const colors = useColors();
  const [containerSize, setContainerSize] = useSafeState({
    width: 0,
    height: 0,
  });
  const [imageSize, setImageSize] = useSafeState({
    width: 0,
    height: 0,
  });

  const imageDimension = useMemo(() => {
    if (containerSize.width < imageSize.width) {
      const aspectRatio = imageSize.width / imageSize.height;
      return {
        width: containerSize.width,
        height: containerSize.width / aspectRatio,
      };
    }

    return { width: imageSize.width, height: imageSize.height };
  }, [containerSize, imageSize]);

  useEffect(() => {
    if (imageUrl) {
      Image.getSize(imageUrl, (width, height) => {
        setImageSize({ width, height });
      });
    }
  }, [imageUrl, setImageSize]);

  if (!imageUrl) {
    return null;
  }

  return (
    <LessonAccentBanner
      style={styles.lessonBannerContainer}
      accentStyle={
        showBanner ? styles.accentBanner : styles.accentWithoutBanner
      }
    >
      <View
        style={[styles.container, style]}
        onLayout={(e) => {
          setContainerSize({
            width: e.nativeEvent.layout.width,
            height: e.nativeEvent.layout.height,
          });
        }}
      >
        <View style={styles.backgroundContainer}>
          <View
            style={[
              styles.background,
              {
                backgroundColor: colors.primaryLight,
                maxHeight: imageDimension.height,
              },
            ]}
          />
        </View>
        <ImageRetry
          accessibilityIgnoresInvertColors
          source={{ uri: imageUrl }}
          style={[styles.image, imageDimension]}
        />
      </View>
    </LessonAccentBanner>
  );
};

export default LessonImage;

const styles = StyleSheet.create({
  lessonBannerContainer: {
    paddingVertical: 0,
    marginTop: -16,
    zIndex: 0,
    marginBottom: 12,
  },
  accentBanner: {
    maxHeight: breakpoint(SizeBreakpoint.SM) ? 80 : 60,
  },
  accentWithoutBanner: {
    maxHeight: 0,
  },
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...maxContainerWidthStyle,
  },
  image: {
    maxWidth: '100%',
    borderRadius: 8,
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  background: {
    width: '100%',
    height: '80%',
    borderRadius: 8,
  },
});
